import { Injectable, computed, signal } from '@angular/core';
import { Me } from './auth.models';

/**
 * Stato dell'utente nel browser. Contiene solo ciò che `/api/auth/me` restituisce: la sessione
 * vive nel cookie HttpOnly e nessun token viene mai salvato nel browser (FR-009).
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly current = signal<Me | null>(null);
  private readonly pendingNotice = signal<string | null>(null);

  readonly user = this.current.asReadonly();
  readonly isAuthenticated = computed(() => this.current() !== null);
  readonly initials = computed(() => this.current()?.initials ?? '');
  readonly userId = computed(() => this.current()?.id ?? null);
  /** Versione della foto del profilo, per `bs-avatar`; `null` senza foto. */
  readonly avatarVersion = computed(() => this.current()?.avatarVersion ?? null);
  /** Dati del profilo necessari e mancanti (FR-005). */
  readonly profileAttention = computed(() => this.current()?.profileAttention ?? 0);
  readonly protectionLevel = computed(() => this.current()?.protectionLevel ?? 'base');
  readonly fullName = computed(() => {
    const user = this.current();
    return user ? `${user.firstName} ${user.lastName}`.trim() : '';
  });
  /** Avviso da mostrare nella pagina di accesso, per esempio la sessione scaduta. */
  readonly notice = this.pendingNotice.asReadonly();

  set(user: Me): void {
    this.current.set(user);
  }

  /** Aggiorna alcuni campi dell'utente corrente, per esempio il nome dopo il salvataggio. */
  patch(changes: Partial<Me>): void {
    const user = this.current();
    if (user) {
      this.current.set({ ...user, ...changes });
    }
  }

  clear(): void {
    this.current.set(null);
  }

  setNotice(message: string): void {
    this.pendingNotice.set(message);
  }

  /** Restituisce l'avviso e lo cancella, così compare una volta sola. */
  consumeNotice(): string | null {
    const notice = this.pendingNotice();
    this.pendingNotice.set(null);
    return notice;
  }
}
